import React, { useRef } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Upload, Download, Paperclip, X, Eye } from "lucide-react";
import { uploadFile, downloadFile } from "@/lib/upload";
import { toast } from "sonner";
import AttachmentPreview from "@/components/AttachmentPreview";

/**
 * Renders a file attachment control for CRUD form dialogs.
 * value = { path, name } (stored on editing[fieldKey])
 * onChange(newValue|null)
 */
export default function FileAttachmentField({ value, onChange, testid, readOnly = false }) {
  const ref = useRef();
  const [uploading, setUploading] = React.useState(false);

  const pick = () => ref.current?.click();
  const handle = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploading(true);
    const res = await uploadFile(f);
    setUploading(false);
    e.target.value = "";
    if (res) { onChange({ path: res.storage_path, name: res.original_filename, size: res.size }); toast.success("File terunggah"); }
  };

  return (
    <div className="space-y-1.5">
      <input ref={ref} type="file" className="hidden" onChange={handle} data-testid={testid ? `${testid}-input` : undefined} accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.webp,.txt,.csv" />
      {value?.path ? (
        <div className="flex items-center gap-2 p-2 rounded-lg border border-slate-200 bg-slate-50">
          <Paperclip className="w-4 h-4 text-[#00A651] shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-xs font-medium text-slate-800 truncate">{value.name || "Lampiran"}</div>
            {value.size && <div className="text-[10px] text-slate-500">{(value.size/1024).toFixed(1)} KB</div>}
          </div>
          <AttachmentPreview value={value} trigger={<Button type="button" size="sm" variant="ghost" title="Preview" data-testid={testid ? `${testid}-preview` : undefined}><Eye className="w-4 h-4"/></Button>} />
          <Button type="button" size="sm" variant="ghost" onClick={()=>downloadFile(value.path, value.name)} title="Unduh" data-testid={testid ? `${testid}-download` : undefined}><Download className="w-4 h-4"/></Button>
          {!readOnly && <Button type="button" size="sm" variant="ghost" onClick={()=>onChange(null)} title="Hapus" className="text-red-600"><X className="w-4 h-4"/></Button>}
        </div>
      ) : !readOnly ? (
        <Button type="button" size="sm" variant="outline" onClick={pick} disabled={uploading} data-testid={testid} className="w-full justify-start">
          <Upload className="w-4 h-4 mr-2"/> {uploading ? "Mengunggah..." : "Pilih File Lampiran"}
        </Button>
      ) : (
        <div className="text-xs text-slate-400 italic">Tidak ada lampiran</div>
      )}
    </div>
  );
}

export function AttachmentBadge({ path, name, testid }) {
  if (!path) return <span className="text-xs text-slate-400">—</span>;
  const value = { path, name };
  return (
    <div className="flex items-center gap-1">
      <AttachmentPreview value={value} trigger={
        <button className="flex items-center gap-1 text-xs text-[#00A651] hover:underline" title={`Preview ${name || ""}`} data-testid={testid ? `${testid}-preview` : undefined}>
          <Eye className="w-3 h-3" /> <span className="max-w-[80px] truncate">{name || "Preview"}</span>
        </button>
      }/>
      <button onClick={()=>downloadFile(path, name)} className="text-slate-400 hover:text-[#00A651]" title="Unduh" data-testid={testid ? `${testid}-download` : undefined}><Download className="w-3 h-3"/></button>
    </div>
  );
}
